import { SectionPaginationApi } from './nested-pagination.types';

/** Maps a table row id (the last loaded row of a section) → the section it belongs to. */
export type LastRowSectionMap = Map<string, { sectionId: string }>;

/**
 * Structural description of the rendered tree, supplied by the screen. Domain-agnostic:
 * the generic walker only needs to know how to get a node's id, its loaded children, and
 * whether a node's children form a paginating section.
 */
export interface NestedTreeWalker<TNode> {
  /** Top-level (enriched) nodes. */
  roots: TNode[];
  /** Row id for a node — equals its section id when it has paginated children. */
  getRowId: (node: TNode) => string;
  /** Loaded children of a node (empty array for a leaf). */
  getChildren: (node: TNode) => TNode[];
  /** True if this node's children are a paginating section (may have a tail to watch). */
  isPaginatedSection: (node: TNode) => boolean;
  isExpanded: (rowId: string) => boolean;
}

/**
 * Builds the "observe this row to load the next page of this section" mappings.
 *
 * Walks the expanded tree and, for each expanded paginated section that still has a next
 * page, records its REAL last loaded child row → the section id. Only section tails are
 * included, so the IntersectionObserver watches a section's tail and nothing else — which
 * is what prevents false triggers from rows below a section.
 */
export const buildLastRowSectionMap = <TNode>(
  walker: NestedTreeWalker<TNode>,
  sectionPagination: SectionPaginationApi
): LastRowSectionMap => {
  const map: LastRowSectionMap = new Map();

  const visit = (node: TNode) => {
    const rowId = walker.getRowId(node);
    if (!walker.isExpanded(rowId)) return;

    const children = walker.getChildren(node);
    if (children.length === 0) return;

    if (walker.isPaginatedSection(node) && sectionPagination.getSection(rowId)?.hasNextPage) {
      const lastChild = children[children.length - 1];
      map.set(walker.getRowId(lastChild), { sectionId: rowId });
    }

    for (const child of children) {
      visit(child);
    }
  };

  for (const root of walker.roots) {
    visit(root);
  }

  return map;
};
